import { z } from 'zod/v4'
import { AdFormat } from '../adFormat'

export const AD_UNIT_NAME_REGEX = /^[^'",;:\\?<>=\s]+$/
export const AD_UNIT_NAME_ERROR = 'Spaces, quotes and the following characters are forbidden: ,;:\\?<>='
export const AD_UNIT_ENVS = ['web', 's2s', 'inapp', 'amp'] as const
export const AdUnitEnvSchema = z.enum(AD_UNIT_ENVS)
export const isValidAdUnitEnv = (env: string): env is AdUnitEnv => {
  return AD_UNIT_ENVS.includes(env)
}

export type AdUnitEnv = z.infer<typeof AdUnitEnvSchema>

// Schéma global
export const PbsAdunitSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1, { message: 'Name is required' }).regex(AD_UNIT_NAME_REGEX, { message: AD_UNIT_NAME_ERROR }),
  enabled: z.boolean().default(true),
  scopeId: z.uuid(),
  updatedAt: z.date(),
  updatedBy: z.string(),
  env: AdUnitEnvSchema.default('s2s'),
})

export type PbsAdUnit = z.infer<typeof PbsAdunitSchema> & { adFormat?: AdFormat }

export const CreatePbsAdUnitSchema = PbsAdunitSchema.omit({
  id: true,
  updatedAt: true,
  updatedBy: true,
  scopeId: true,
})
export type CreatePbsAdUnit = z.infer<typeof CreatePbsAdUnitSchema> & { adFormat?: AdFormat }

export const CreatePublicPbsAdUnitSchema = CreatePbsAdUnitSchema.extend({
  env: z.literal('s2s', { message: `'env' is auto-set. If provided, it must be 's2s'` }).default('s2s'),
})
export const UpdatePbsAdUnitSchema = PbsAdunitSchema.omit({
  updatedAt: true,
  updatedBy: true,
})
export type UpdatePbsAdUnit = z.infer<typeof UpdatePbsAdUnitSchema>

export type PbsAdUnitBulkCreateResponse = { value?: PbsAdUnit | CreatePbsAdUnit; error?: string }

/** Bounds a single batch, so a large paste can't build an unbounded `$in`. */
export const VALIDATE_AD_UNIT_NAMES_MAX_BATCH = 1000

/** `env` is required: a name is taken per env, so an answer without one would be about the wrong list. */
export const ValidateAdUnitNamesRequestSchema = z.object({
  names: z.array(z.string()).max(VALIDATE_AD_UNIT_NAMES_MAX_BATCH),
  env: AdUnitEnvSchema,
})
export type ValidateAdUnitNamesRequest = z.infer<typeof ValidateAdUnitNamesRequestSchema>

export type ValidateAdUnitNamesResponse = { takenNames: string[] }

/**
 * The CSV columns a node ad units file carries, per env.
 *
 * One definition for the importer, the downloadable template and the export, because the parser
 * validates headers exactly — a missing column, an unexpected one, or the wrong count all fail the
 * whole file. Three copies of these strings meant an export could only round-trip by coincidence.
 *
 * Server-side and AMP carry a name and nothing else: AMP's format is fixed at import, and a
 * server-side ad unit has no format at all. In-app is the one env where the format is the user's
 * choice, so it is the one env with a second column.
 */
export const PBS_AD_UNIT_CSV_HEADERS = ['name'] as const
export const PBS_AD_UNIT_CSV_INAPP_HEADERS = ['name', 'format'] as const

export const pbsAdUnitCsvHeaders = (env: AdUnitEnv): readonly string[] => (env === 'inapp' ? PBS_AD_UNIT_CSV_INAPP_HEADERS : PBS_AD_UNIT_CSV_HEADERS)

/** The in-app format names the importer accepts, keyed by the ad format id the app stores. */
const INAPP_CSV_FORMAT_BY_AD_FORMAT_ID: Record<string, string> = {
  APP_BANNER: 'Banner',
  APP_INTERSTITIAL: 'Interstitial',
  APP_NATIVE: 'Native',
}

/** Quote whatever would otherwise break the row apart, doubling the inner quotes as the CSV format wants. */
const csvCell = (value: string): string => (/[",\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value)

/**
 * Builds the node ad units export downloaded from a node ad units tab.
 *
 * Written against `pbsAdUnitCsvHeaders` so the file it produces is one the matching importer accepts:
 * export a tab, re-import the untouched file, and every row is recognised (and skipped as already
 * taken). `env` is not a column — the import route carries it in its path, and an extra header would
 * fail the strict header check.
 */
export const buildPbsAdUnitsExportCSV = (adUnits: Pick<PbsAdUnit, 'name' | 'adFormat'>[], env: AdUnitEnv): string => {
  const headers = pbsAdUnitCsvHeaders(env)
  const row = (adUnit: Pick<PbsAdUnit, 'name' | 'adFormat'>): string[] => (env === 'inapp' ? [adUnit.name, INAPP_CSV_FORMAT_BY_AD_FORMAT_ID[adUnit.adFormat?.id ?? ''] ?? 'Banner'] : [adUnit.name])

  return [headers.join(','), ...adUnits.map((adUnit) => row(adUnit).map(csvCell).join(','))].join('\n') + '\n'
}

/**
 * The key a name is unique under: the env qualifies it, because the same name is free again in another one.
 * `:` is safe as a separator — AD_UNIT_NAME_REGEX forbids it in a name.
 */
export const takenNameKey = ({ name, env }: Pick<PbsAdUnit, 'name' | 'env'>): string => `${env}:${name}`

/** How the envs rank when one name is held by several ad units. Earlier wins. */
const CANONICAL_ENV_ORDER: readonly AdUnitEnv[] = ['web', 's2s', 'inapp', 'amp']

/**
 * Which ad unit a shared name resolves to, within one collection.
 *
 * The choice has to be stable across runs, not merely consistent within one: the emitted stack
 * config is keyed by name, and `findEnabledByScopeId` sorts by name alone, so the order two
 * same-named rows arrive in is undefined. Picking whichever came first would make the snapshot hash
 * flap between builds and mark stacks out of sync at random. Env rank first, then id as the
 * tie-break, so the answer only depends on the data.
 *
 * `env` is required: an absent one used to rank last, standing for "this is One's", which a Node ad
 * unit predating the field also matched. Callers narrow to one collection first, and One's ad units
 * reach the builders through `stackAdUnitOf` with the `web` they are by construction.
 */
export const pickCanonicalAdUnit = <T extends { id: string; env: AdUnitEnv }>(candidates: T[]): T | undefined => {
  // -1 for an env outside the order sorts last rather than first. The store casts rather than parses,
  // so a value that never went through the schema arrives as written.
  const rank = (adUnit: T): number => {
    const index = CANONICAL_ENV_ORDER.indexOf(adUnit.env)
    return index === -1 ? CANONICAL_ENV_ORDER.length : index
  }

  return candidates.reduce<T | undefined>((best, candidate) => {
    if (!best) return candidate
    const [candidateRank, bestRank] = [rank(candidate), rank(best)]
    if (candidateRank !== bestRank) return candidateRank < bestRank ? candidate : best
    return candidate.id < best.id ? candidate : best
  }, undefined)
}

/**
 * One ad unit per name, because a name is what identifies an ad unit downstream.
 *
 * Names are unique per (scope, env), so the same one can be held once in each env — but the stack
 * config we emit is keyed by name and Prebid Server matches on that name at auction time, which
 * makes two same-named rows one ad unit code. Collapsing them here is what stops the builders
 * merging their bidders, duplicating a media type, or overwriting one another's floor.
 *
 * `collisions` is what was collapsed, for the caller to report: nothing can create this today, so
 * seeing one means a scope has ad units the config cannot tell apart.
 */
export const dedupeAdUnitsByName = <T extends { id: string; name: string; env: AdUnitEnv }>(adUnits: readonly T[]): { adUnits: T[]; collisions: { name: string; adUnits: T[] }[] } => {
  const byName = new Map<string, T[]>()
  for (const adUnit of adUnits) {
    const group = byName.get(adUnit.name)
    if (group) group.push(adUnit)
    else byName.set(adUnit.name, [adUnit])
  }

  const deduped: T[] = []
  const collisions: { name: string; adUnits: T[] }[] = []
  for (const [name, group] of byName) {
    // Keeping the input order of the survivors, so a list with no collision comes back untouched.
    deduped.push(group.length === 1 ? group[0] : pickCanonicalAdUnit(group)!)
    if (group.length > 1) collisions.push({ name, adUnits: group })
  }

  return { adUnits: deduped, collisions }
}
